/* eslint-disable max-lines */
import { CdkDrag, CdkDragEnd, CdkDragHandle } from '@angular/cdk/drag-drop';
import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  ClientToolsRegistry,
  TellentAssistantConfigService,
  TellentAssistantService,
  TellentAssistantThreadsService,
  ToolTitlesFacade,
  type AssistantNewConversationTrigger,
} from '@core/tellent-assistant-logic';
import type { AskDecisionOption, ConversationStarter } from '@core/tellent-assistant-types';
import {
  injectTellentAssistantExternalLinkOpener,
  injectTellentAssistantLinkOpener,
  TellentAssistantEmptyStateComponent,
  TellentAssistantExternalLinksDirective,
  TellentAssistantHeaderComponent,
  TellentAssistantInputComponent,
  TellentAssistantMessageComponent,
  TellentAssistantRunErrorComponent,
  TellentAssistantThreadDrawerComponent,
} from '@core/tellent-assistant-ui';
import { AppKind, ORG_GUID } from '@core/tellent-types';
import { injectTellentAppUrlBuilder } from '@core/tellent-util';
import {
  ConnectedOverlayDirective,
  FIXED_OVERLAY_LEVEL,
  IconComponent,
  SpinnerComponent,
  WindowRef,
} from '@core/user-interface-legacy';

import { ChatPanelAnalyticsService } from './chat-panel-analytics.service';
import { ChatPanelProposalsService } from './chat-panel-proposals.service';
import { TellentAssistantScrollAnchorService } from './tellent-assistant-scroll-anchor.service';
import { ThreadOptionsSheetService } from './thread-options-sheet.service';

export type TellentAssistantMode = 'compact' | 'fullscreen' | 'minimized';

@Component({
  selector: 'tellent-assistant-chat-panel',
  imports: [
    TellentAssistantHeaderComponent,
    TellentAssistantEmptyStateComponent,
    TellentAssistantInputComponent,
    TellentAssistantThreadDrawerComponent,
    TellentAssistantMessageComponent,
    TellentAssistantRunErrorComponent,
    ConnectedOverlayDirective,
    IconComponent,
    NgTemplateOutlet,
    SpinnerComponent,
    TranslatePipe,
    CdkDrag,
    CdkDragHandle,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tellent-assistant-chat-panel.component.html',
  styleUrl: './tellent-assistant-chat-panel.component.less',
  providers: [
    TellentAssistantScrollAnchorService,
    ThreadOptionsSheetService,
    ChatPanelProposalsService,
  ],
  host: {
    '[class.expanded]': 'expanded()',
    '[class.minimized]': 'minimized()',
    '[class.first-paint-done]': 'firstPaintDone()',
    '[class.embedded]': 'embedded()',
  },
  // One delegated listener for every external link the panel renders.
  hostDirectives: [TellentAssistantExternalLinksDirective],
})
export class TellentAssistantChatPanelComponent {
  private readonly i18n = inject(I18nService);
  private readonly windowRef = inject(WindowRef);
  private readonly orgGuid = inject(ORG_GUID, { optional: true });
  private readonly urlBuilder = injectTellentAppUrlBuilder();
  private readonly linkOpener = injectTellentAssistantLinkOpener({ optional: true });
  private readonly externalOpener = injectTellentAssistantExternalLinkOpener({ optional: true });
  private readonly drawerOverlay = viewChild<ConnectedOverlayDirective>('drawerOverlay');
  private readonly bodyScroll = viewChild<ElementRef<HTMLDivElement>>('bodyScroll');
  private readonly scrollSpacer = viewChild<ElementRef<HTMLDivElement>>('scrollSpacer');
  private readonly inputRef = viewChild(TellentAssistantInputComponent);
  private readonly clientToolsRegistry = inject(ClientToolsRegistry);
  private readonly toolTitlesFacade = inject(ToolTitlesFacade);
  private readonly scrollAnchor = inject(TellentAssistantScrollAnchorService);
  protected readonly analytics = inject(ChatPanelAnalyticsService);
  protected readonly proposals = inject(ChatPanelProposalsService);

  protected readonly headerRef = viewChild(TellentAssistantHeaderComponent);
  protected readonly assistantService = inject(TellentAssistantService);
  protected readonly threadsService = inject(TellentAssistantThreadsService);
  protected readonly config = inject(TellentAssistantConfigService);
  protected readonly toolTitles = this.toolTitlesFacade.titles;
  protected readonly threadOptionsSheet = inject(ThreadOptionsSheetService);
  protected readonly drawerOpen = this.threadOptionsSheet.drawerOpen;
  protected readonly firstPaintDone = signal<boolean>(false);
  protected readonly defaultTitle = this.i18n.translate('tlc.tellent_assistant.chat_panel.title');
  protected readonly currentThread = computed(() => {
    const id = this.assistantService.currentThreadId();
    if (!id) return null;
    return this.threadsService.threads().find(t => t.id === id) ?? null;
  });
  protected readonly title = computed(() => this.currentThread()?.title || this.defaultTitle);
  protected readonly currentThreadPinned = computed(() => this.currentThread()?.pinned ?? false);
  protected readonly drawerPositions = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  ] satisfies ConnectionPositionPair[];
  protected readonly expanded = computed(() => this.mode() === 'fullscreen');
  protected readonly minimized = computed(() => this.mode() === 'minimized');
  protected readonly dragPosition = computed(() =>
    this.expanded() ? { x: 0, y: 0 } : { x: this.dockOffsetX(), y: 0 },
  );

  protected readonly scrollToBottomOnThreadOpen = effect(() => {
    const id = this.assistantService.currentThreadId();
    const msgs = this.assistantService.messages();
    const host = this.bodyScroll()?.nativeElement;
    if (!host || !id) return;
    if (id === this.lastScrolledThreadId) return;
    if (msgs.length === 0) return;
    this.lastScrolledThreadId = id;
    requestAnimationFrame(() => {
      host.scrollTop = host.scrollHeight;
    });
  });

  protected readonly anchorPromptOnSend = effect(() => {
    const threadId = this.assistantService.currentThreadId();
    const hasMessages = this.assistantService.messages().length > 0;
    const host = this.bodyScroll()?.nativeElement;
    const spacer = this.scrollSpacer()?.nativeElement;
    if (!host || !spacer) return;
    this.scrollAnchor.sync({ threadId, hasMessages, host, spacer });
  });

  protected readonly atBottom = signal<boolean>(true);

  protected readonly refreshFadeOnContent = effect(() => {
    this.assistantService.messages();
    this.assistantService.runStatus();
    const host = this.bodyScroll()?.nativeElement;
    if (!host) return;
    requestAnimationFrame(() => this.updateAtBottom(host));
  });

  protected readonly clearInputOnThreadSwitch = effect(() => {
    const id = this.assistantService.currentThreadId();
    const input = this.inputRef();
    if (this.previousThreadId === undefined) {
      this.previousThreadId = id;
      return;
    }
    if (id === this.previousThreadId) return;
    this.previousThreadId = id;
    input?.clear();
  });

  // Focus the composer whenever the window is revealed: opened from the
  // trigger (first render) or restored from the minimized state (header /
  // expand icon). Plain compact <-> fullscreen toggles and the embedded
  // variant don't refocus, so we never steal focus mid-typing.
  protected readonly focusInputOnReveal = effect(() => {
    const mode = this.mode();
    const input = this.inputRef();
    const embedded = this.embedded();
    untracked(() => {
      const previous = this.previousMode;
      this.previousMode = mode;
      if (embedded) return;
      if ((previous === undefined || previous === 'minimized') && mode !== 'minimized') {
        this.pendingFocus = true;
        // Skip when the reveal coincides with an auto-sent message (e.g. a
        // suggestion banner opens the window and immediately sends a starter):
        // the run is already 'running' at reveal, so we don't pull focus into
        // the composer while the assistant is responding.
        this.suppressFocusForRun = this.assistantService.runStatus() === 'running';
      }
      if (!this.pendingFocus || !input) return;
      this.pendingFocus = false;
      if (this.suppressFocusForRun) return;
      // Defer a frame so the composer is laid out before focusing: when
      // restoring from minimized its footer switches from display:none, and
      // focusing it in the same tick silently no-ops.
      requestAnimationFrame(() => input.focus());
    });
  });

  public readonly mode = input.required<TellentAssistantMode>();
  public readonly dockOffsetX = input<number>(0);
  public readonly embedded = input<boolean>(false);
  public readonly close = output<void>();
  public readonly expand = output<void>();
  public readonly collapse = output<void>();
  public readonly minimize = output<void>();
  public readonly restore = output<void>();
  public readonly dockOffsetXChange = output<number>();

  public readonly renderToolNames = this.clientToolsRegistry.ids;

  private lastScrolledThreadId: string | null = null;
  private previousThreadId: string | null | undefined = undefined;
  private previousMode: TellentAssistantMode | undefined = undefined;
  private pendingFocus = false;
  private suppressFocusForRun = false;
  private dragged = false;

  constructor() {
    this.toolTitlesFacade.ensureLoaded();
    afterNextRender(() => this.firstPaintDone.set(true));
  }

  public onDragStarted(): void {
    this.dragged = true;
  }

  public onDragEnded(event: CdkDragEnd): void {
    this.analytics.windowDragged();
    if (this.expanded()) return;
    this.dockOffsetXChange.emit(event.source.getFreeDragPosition().x);
  }

  public onDragMoved(): void {
    this.drawerOverlay()?.overlayRef?.updatePosition();
  }

  public onHeaderClick(): void {
    if (this.dragged) {
      this.dragged = false;
      return;
    }
    if (this.minimized()) this.restore.emit();
  }

  public readonly isDeleting = (id: string): boolean => this.threadsService.isThreadDeleting(id);

  public readonly resolveRenderComponent = (
    toolName: string,
    _args: unknown,
    status: 'running' | 'done' | 'error',
  ) =>
    this.clientToolsRegistry.shouldRender(toolName, status)
      ? this.clientToolsRegistry.byId(toolName)?.component
      : undefined;

  protected onClose(): void {
    this.analytics.windowClosed();
    this.close.emit();
  }

  protected onMinimize(): void {
    this.analytics.windowMinimized();
    this.minimize.emit();
  }

  protected onExpand(): void {
    this.analytics.fullscreenModeChanged('fullscreen');
    this.expand.emit();
  }

  protected onCollapse(): void {
    this.analytics.fullscreenModeChanged('small');
    this.collapse.emit();
  }

  protected toggleDrawer(): void {
    const wasOpen = this.drawerOpen();
    this.analytics.hamburgerToggled(wasOpen);
    this.drawerOpen.set(!wasOpen);
  }

  protected togglePin(): void {
    const thread = this.currentThread();
    if (!thread) return;
    this.threadsService.setThreadPinned(thread.id, !thread.pinned);
  }

  protected onDrawerAttach(): void {
    const host = this.drawerOverlay()?.overlayRef?.hostElement;
    if (host) host.style.zIndex = `${FIXED_OVERLAY_LEVEL + 1}`;
  }

  protected onSend(text: string): void {
    this.analytics.messageSent(text);
    this.send(text);
  }

  protected send(text: string, metadata?: Record<string, unknown>): void {
    this.scrollAnchor.arm();
    this.assistantService.send(text, metadata);
  }

  protected onDecisionAnswered(options: AskDecisionOption[]): void {
    const values = options.map(o => o.value).join(', ');
    const labels = options.map(o => o.label).join(', ');
    if (labels !== values) {
      this.send(values, { displayLabel: labels });
    } else {
      this.send(values);
    }
  }

  protected skipDecision(): void {
    this.send(this.i18n.translate('tlc.tellent_assistant.proposal.skip'));
  }

  protected hasLaterUserTurn(index: number): boolean {
    return this.assistantService
      .messages()
      .some((message, i) => i > index && message.role === 'user');
  }

  protected sendStarter(starter: ConversationStarter): void {
    this.analytics.starterClicked(starter.text);
    this.scrollAnchor.arm();
    this.assistantService.sendStarter(starter);
  }

  protected newThread(trigger: AssistantNewConversationTrigger): void {
    this.analytics.newConversationClicked(trigger);
    this.assistantService.newThread();
    this.drawerOpen.set(false);
  }

  protected selectThread(id: string): void {
    this.analytics.conversationLoadedFromHistory();
    this.assistantService.switchThread(id);
    this.drawerOpen.set(false);
  }

  protected openHelpCenter(): void {
    const url = this.i18n.translate('tlc.support_resources.help_center');
    if (!this.externalOpener?.(url)) this.windowRef.nativeWindow.open(url, '_blank');
  }

  protected openIntelligenceSettings(): void {
    if (!this.orgGuid) return;
    const page = { id: <const>'intelligence' };
    const url = this.urlBuilder(AppKind.TellentAdmin, page, this.orgGuid);
    if (this.linkOpener?.({ appKind: AppKind.TellentAdmin, page, url })) return;
    this.windowRef.nativeWindow.location = url;
  }

  protected onBodyScroll(): void {
    const host = this.bodyScroll()?.nativeElement;
    if (host) this.updateAtBottom(host);
  }

  private updateAtBottom(host: HTMLElement): void {
    this.atBottom.set(host.scrollHeight - host.scrollTop - host.clientHeight <= 1);
  }
}
